import { error } from '@sveltejs/kit';
import { client, queryOptions } from "$lib/apollo";
import gql from "graphql-tag";

export const load = async ({ params, session }) => {
  try {
    const { data } = await client.query({
      variables: {
        uid: params.slug || "__home__",
      },
      query: gql`
          query page($uid: String!) {
            allPages(uid: $uid) {
              edges {
                node {
                  _meta {
                    uid
                    type
                  }
                  title
                  description
                  body {
                    __typename
                    ... on PageBodyHero {
                      primary {
                        vimeo
                        fallback
                        taller
                      }
                    }
                    ... on PageBodyPanorama {
                      primary {
                        top_margin
                        panorama
                      }
                    }
                    ... on PageBodyHeadline {
                      primary {
                        headline
                        background
                        pattern
                      }
                    }
                    ... on PageBodyHero_image {
                      primary {
                        hero
                        ultrawide
                      }
                    }
                    ... on PageBodyEmbed {
                      primary {
                        embed
                      }
                    }
                    ... on PageBodyGallery {
                      slides: fields {
                        hover_text
                        headline
                        subline
                        text
                        image
                        image_alt
                        button_link {
                          _linkType
                          ... on Page {
                            _meta {
                              uid
                              type
                            }
                          }
                          ... on Case {
                            _meta {
                              uid
                              type
                            }
                          }
                        }
                        button_text
                      }
                    }
                    ... on PageBodyText {
                      primary {
                        headline
                        subline
                        text
                        background
                        pattern
                        reverse
                        fancylist
                        image
                      }
                      fields {
                        link {
                          _linkType
                          ... on Page {
                            title
                            _meta {
                              uid
                              type
                            }
                          }
                          ... on _ExternalLink {
                            url
                            target
                          }
                          ... on _FileLink {
                            url
                            name
                          }
                        }
                        link_text
                      }
                    }
                    ... on PageBodySlider {
                      fields {
                        headline
                        text
                      }
                    }
                    ... on PageBodyCustomers {
                      fields {
                        customer {
                          ... on Customer {
                            customer
                            logo {
                              ... on _ImageLink {
                                url
                              }
                            }
                          }
                        }
                      }
                    }
                    ... on PageBodyEmployees {
                      primary {
                        tiles
                      }
                      fields {
                        employee {
                          ... on Employee {
                            _meta {
                              uid
                              type
                            }
                            name
                            position
                            text
                            image
                            detail_text
                            detail_image
                          }
                        }
                      }
                    }
                    ... on PageBodyCases {
                      primary {
                        tiles
                        background
                        pattern
                      }
                      fields {
                        case {
                          ... on Case {
                            headline
                            subline
                            image
                            _meta {
                              uid
                              type
                            }
                            customer {
                              ... on Customer {
                                logo {
                                  ... on _ImageLink {
                                    url
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                    ... on PageBodyQuotes {
                      fields {
                        image
                        quote
                        customer {
                          ... on Customer {
                            customer
                            logo {
                              ... on _ImageLink {
                                url
                              }
                            }
                          }
                        }
                        position
                        name
                        background
                      }
                    }
                    ... on PageBodyAwards {
                      fields {
                        image
                      }
                    }
                    ... on PageBodyCta {
                      primary {
                        text
                        background_color: background
                        button_text
                        button_link {
                          _linkType
                          ... on Page {
                            _meta {
                              uid
                              type
                            }
                          }
                          ... on Case {
                            _meta {
                              uid
                              type
                            }
                          }
                          ... on _ExternalLink {
                            url
                            target
                          }
                          ... on _FileLink {
                            url
                            name
                          }
                        }
                      }
                    }
                    ... on PageBodyClickup {
                      primary {
                        clickup
                      }
                    }
                    ... on PageBodyPartner {
                      primary {
                        columns
                        background
                      }
                      fields {
                        logo {
                          ... on _ImageLink {
                            url
                          }
                        }
                        headline
                        text
                        button_link {
                          _linkType
                          ... on Page {
                            title
                            _meta {
                              uid
                              type
                            }
                          }
                          ... on _ExternalLink {
                            url
                            target
                          }
                          ... on _FileLink {
                            url
                            name
                          }
                        }
                        button_text
                      }
                    }
                  }
                  social {
                    ... on PageSocialGeneral_card {
                      primary {
                        title
                        description
                        image
                      }
                    }
                    ... on PageSocialTwitter_card {
                      primary {
                        card_type
                        twitter_handle
                        title
                        description
                        image
                      }
                    }
                  }
                }
              }
            }
          }
        `,
      ...queryOptions(session),
    });
    if (data.allPages.edges.length) {
      const page = data.allPages.edges[0].node;
      for (let block of page.body || []) {
        switch (block.__typename) {
          case "PageBodyGallery":
            block.__typename = "PageBodyLightboxGallery";
            break;
          case "PageBodyCustomers":
            if (!block.fields.length) {
              block.fields = (
                await client.query({
                  query: gql`
                      query customers {
                        allCustomers {
                          edges {
                            node {
                              customer
                              logo {
                                ... on _ImageLink {
                                  url
                                }
                              }
                            }
                          }
                        }
                      }
                    `,
                })
              ).data.allCustomers.edges.map((edge) => ({
                customer: edge.node,
              }));
            }
            break;
          case "PageBodyEmployees":
            if (!block.fields.length) {
              block.fields = (
                await client.query({
                  query: gql`
                      query employees {
                        allEmployees {
                          edges {
                            node {
                              _meta {
                                uid
                                type
                              }
                              name
                              position
                              text
                              image
                            }
                          }
                        }
                      }
                    `,
                })
              ).data.allEmployees.edges.map((edge) => ({
                employee: edge.node,
              }));
            }
            break;
          case "PageBodyCases":
            if (!block.fields.length) {
              block.fields = (
                await client.query({
                  query: gql`
                      query cases {
                        allCases(where: { highlighted: true }) {
                          edges {
                            node {
                              headline
                              subline
                              image
                              _meta {
                                uid
                                type
                              }
                              customer {
                                ... on Customer {
                                  logo {
                                    ... on _ImageLink {
                                      url
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    `,
                })
              ).data.allCases.edges.map((edge) => ({
                case: edge.node,
              }));
            }
            break;
          case "PageBodyServices":
            block.fields = (
              await client.query({
                query: gql`
                    query services {
                      allServices {
                        edges {
                          node {
                            color
                            headline
                            subline
                            text
                            links {
                              link {
                                _linkType
                                ... on Page {
                                  title
                                  _meta {
                                    uid
                                    type
                                  }
                                }
                              }
                              link_text
                            }
                          }
                        }
                      }
                    }
                  `,
              })
            ).data.allServices.edges.map((edge) => ({
              service: edge.node,
            }));
        }
      }
      return {
  page,
};
    } else {
      error(404, "Page not found");
    }
  } catch (error) {
    console.error(error);
    throw new Error("@migration task: Migrate this return statement (https://github.com/sveltejs/kit/discussions/5774#discussioncomment-3292693)");
    return {
      status: 500,
      error,
    };
  }
};
